import { useState, useEffect } from 'react';
import { Banner, Button, InlineStack } from '@shopify/polaris';
import { trackEvent } from '../utils/analytics';

const REVIEW_URL = 'https://apps.shopify.com/profit-pulse-ai#modal-show=WriteReviewModal';

/**
 * ReviewPrompt — triggers after a genuine value moment:
 * - A real margin leak was found (profitpulse_first_leak_tracked must be set)
 * - Merchant has visited the app on 3+ separate days
 * - Merchant has resolved (dismissed) at least one Action Item
 *
 * Does NOT prompt on first session, after install, or before any real leak is found.
 * Shows once per merchant (persisted in localStorage).
 */
export default function ReviewPrompt({ shop }) {
  const [showPrompt, setShowPrompt] = useState(false);

  useEffect(() => {
    if (localStorage.getItem('profitpulse_review_prompt_dismissed')) return;

    // Only prompt after a real margin leak was actually found
    if (!localStorage.getItem('profitpulse_first_leak_tracked')) return;

    const visitDays = JSON.parse(localStorage.getItem('profitpulse_visit_days') || '[]');
    if (visitDays.length < 3) return;

    const dismissedActions = JSON.parse(localStorage.getItem('profitpulse_dismissed_actions') || '[]');
    if (dismissedActions.length === 0) return;

    setShowPrompt(true);
    trackEvent('review_prompt_shown', {}, { shop });
  }, [shop]);

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('profitpulse_review_prompt_dismissed', 'true');
  };

  const handleReview = () => {
    trackEvent('review_click', {}, { shop });
    localStorage.setItem('profitpulse_review_prompt_dismissed', 'true');
    window.open(REVIEW_URL, '_blank');
    setShowPrompt(false);
  };

  if (!showPrompt) return null;

  return (
    <Banner
      title="Enjoying Profit Pulse AI?"
      tone="success"
      onDismiss={handleDismiss}
    >
      <p>
        If Profit Pulse AI has helped you catch profit leaks or protect margins, a quick review helps other merchants find us.
      </p>
      <div style={{ marginTop: '8px' }}>
        <InlineStack gap="200">
          <Button onClick={handleReview} variant="primary">Leave a review</Button>
          <Button onClick={handleDismiss}>Not now</Button>
        </InlineStack>
      </div>
    </Banner>
  );
}
